import type { ReactNode } from "react";
import {
  BriefcaseIcon,
  BuildingIcon,
  CalendarIcon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
} from "./Icons";
import { asArray, asObject, asString, getNested } from "../lib/json";
import { formatCnpj } from "../lib/cnpj";
import { formatCep, formatCurrency, formatDate, formatPhone } from "../lib/formatters";
import type { JsonObject, JsonValue } from "../types/json";

interface SummaryProps {
  data: JsonObject;
}

function joinNonEmpty(values: string[], separator = ", "): string {
  return values.map((value) => value.trim()).filter(Boolean).join(separator);
}

function valueOrFallback(value: string, fallback = "Não informado"): string {
  return value.trim() || fallback;
}

function InfoBlock({
  icon,
  label,
  children,
}: {
  icon: ReactNode;
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-[#dce5ef] bg-white p-4">
      <div className="mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-[#6f7f94]">
        <span className="text-[#1c5a95]">{icon}</span>
        {label}
      </div>
      <div className="text-sm leading-6 text-[#17253a]">{children}</div>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-2xl border border-[#dce5ef] bg-[#f4f7fb] p-4">
      <p className="text-xs font-medium text-[#6f7f94]">{label}</p>
      <p className="mt-2 break-words text-sm font-semibold text-[#17253a]">{value}</p>
    </div>
  );
}

export function Summary({ data }: SummaryProps) {
  const establishment = asObject(data.estabelecimento);
  const city = asObject(establishment?.cidade);
  const state = asObject(establishment?.estado);
  const primaryActivity = asObject(establishment?.atividade_principal);
  const porte = asObject(data.porte);

  const companyName = asString(data.razao_social);
  const fantasyName = asString(establishment?.nome_fantasia);
  const situation = asString(establishment?.situacao_cadastral);
  const cnpj = asString(establishment?.cnpj);
  const capital = asString(data.capital_social);
  const openingDate = asString(establishment?.data_inicio_atividade);

  const street = joinNonEmpty(
    [
      asString(establishment?.tipo_logradouro),
      asString(establishment?.logradouro),
      asString(establishment?.numero),
    ],
    " ",
  );
  const complement = asString(establishment?.complemento);
  const neighborhood = asString(establishment?.bairro);
  const cityAndState = joinNonEmpty([asString(city?.nome), asString(state?.sigla)], "/");
  const cep = formatCep(asString(establishment?.cep));

  const phones = [
    formatPhone(asString(establishment?.ddd1), asString(establishment?.telefone1)),
    formatPhone(asString(establishment?.ddd2), asString(establishment?.telefone2)),
  ].filter(Boolean);
  const email = asString(establishment?.email);

  const stateRegistrations = asArray(establishment?.inscricoes_estaduais)
    .map((item) => asObject(item))
    .filter((item): item is JsonObject => Boolean(item));

  const cnaeCode = asString(primaryActivity?.subclasse) || asString(primaryActivity?.id);
  const cnaeDescription = asString(primaryActivity?.descricao);
  const nature = asString(getNested(data, "natureza_juridica", "descricao"));

  const active = situation.toLowerCase() === "ativa";

  return (
    <section className="space-y-5" aria-labelledby="company-summary-title">
      <div className="card overflow-hidden rounded-[2rem]">
        <div className="relative border-b border-[#dce5ef] p-5 sm:p-7">
          <div className="hidden" />
          <div className="relative flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex min-w-0 items-start gap-4">
              <div className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-[#eaf2fa] text-[#1c5a95] ring-1 ring-inset ring-[#c9dbeb]">
                <BuildingIcon className="size-6" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#1c5a95]">Empresa encontrada</p>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ring-1 ring-inset ${
                      active
                        ? "bg-[#e7f5ef] text-[#16835f] ring-[#bfe3d3]"
                        : "bg-[#fbf1e3] text-[#9a5b12] ring-[#efd6b3]"
                    }`}
                  >
                    {valueOrFallback(situation, "Situação não informada")}
                  </span>
                </div>
                <h2 id="company-summary-title" className="mt-2 text-xl font-semibold leading-tight text-[#17253a] sm:text-2xl">
                  {valueOrFallback(companyName)}
                </h2>
                <p className="mt-2 text-sm text-[#6f7f94]">
                  {fantasyName ? `Nome fantasia: ${fantasyName}` : "Nome fantasia não informado"}
                </p>
              </div>
            </div>
            <div className="rounded-2xl border border-[#dce5ef] bg-[#f4f7fb] px-4 py-3 text-left lg:text-right">
              <p className="text-xs text-[#6f7f94]">Última atualização da base</p>
              <p className="mt-1 text-sm font-medium text-[#17253a]">
                {data.atualizado_em ? formatDate(asString(data.atualizado_em)) : "Não informada"}
              </p>
            </div>
          </div>
        </div>

        <div className="grid gap-3 p-5 sm:grid-cols-2 sm:p-7 lg:grid-cols-4">
          <Metric label="CNPJ" value={cnpj ? formatCnpj(cnpj) : "Não informado"} />
          <Metric label="Capital social" value={capital ? formatCurrency(capital) : "Não informado"} />
          <Metric label="Porte" value={valueOrFallback(asString(porte?.descricao))} />
          <Metric label="Início da atividade" value={openingDate ? formatDate(openingDate) : "Não informado"} />
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <InfoBlock icon={<MapPinIcon className="size-4" />} label="Endereço">
          <p>{valueOrFallback(joinNonEmpty([street, complement]))}</p>
          <p className="text-[#6f7f94]">
            {valueOrFallback(joinNonEmpty([neighborhood, cityAndState, cep]))}
          </p>
        </InfoBlock>

        <InfoBlock icon={<PhoneIcon className="size-4" />} label="Contato">
          {phones.length > 0 ? phones.map((phone) => <p key={phone}>{phone}</p>) : <p>Telefone não informado</p>}
          <p className="mt-1 break-all text-[#6f7f94]">
            <MailIcon className="mr-1 inline size-3.5" />
            {valueOrFallback(email, "E-mail não informado")}
          </p>
        </InfoBlock>

        <InfoBlock icon={<BriefcaseIcon className="size-4" />} label="Atividade principal">
          <p className="font-medium text-[#17253a]">{valueOrFallback(cnaeDescription)}</p>
          <p className="mt-1 text-[#6f7f94]">CNAE {valueOrFallback(cnaeCode)}</p>
        </InfoBlock>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1.2fr_1fr]">
        <div className="card rounded-[1.75rem] p-5 sm:p-6">
          <div className="mb-4 flex items-center gap-2">
            <CalendarIcon className="size-4 text-[#1c5a95]" />
            <h3 className="text-sm font-semibold text-[#17253a]">Dados cadastrais</h3>
          </div>
          <dl className="grid gap-4 sm:grid-cols-2">
            <div>
              <dt className="text-xs text-[#6f7f94]">Natureza jurídica</dt>
              <dd className="mt-1 text-sm text-[#17253a]">{valueOrFallback(nature)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[#6f7f94]">Tipo do estabelecimento</dt>
              <dd className="mt-1 text-sm text-[#17253a]">{valueOrFallback(asString(establishment?.tipo))}</dd>
            </div>
            <div>
              <dt className="text-xs text-[#6f7f94]">Município</dt>
              <dd className="mt-1 text-sm text-[#17253a]">{valueOrFallback(cityAndState)}</dd>
            </div>
            <div>
              <dt className="text-xs text-[#6f7f94]">País</dt>
              <dd className="mt-1 text-sm text-[#17253a]">
                {valueOrFallback(asString(getNested(establishment, "pais", "nome")))}
              </dd>
            </div>
          </dl>
        </div>

        <div className="card rounded-[1.75rem] p-5 sm:p-6">
          <h3 className="text-sm font-semibold text-[#17253a]">Inscrições estaduais</h3>
          <div className="mt-4 flex max-h-44 flex-wrap gap-2 overflow-auto pr-1">
            {stateRegistrations.length > 0 ? (
              stateRegistrations.map((registration, index) => {
                const registrationState = asObject(registration.estado);
                const registrationNumber = asString(registration.inscricao_estadual);
                const isActive = registration.ativo === true;
                return (
                  <span
                    key={`${registrationNumber}-${index}`}
                    className="inline-flex items-center gap-2 rounded-xl border border-[#dce5ef] bg-[#f4f7fb] px-3 py-2 text-xs text-[#17253a]"
                  >
                    <span className={`size-1.5 rounded-full ${isActive ? "bg-[#16835f]" : "bg-[#c9d4e0]"}`} />
                    {asString(registrationState?.sigla) || "UF"}: {registrationNumber || "Não informada"}
                  </span>
                );
              })
            ) : (
              <p className="text-sm text-[#6f7f94]">Nenhuma inscrição estadual informada.</p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
